import { FocusEvent, ReactElement } from 'react'
import { ContentCopy, Delete, Link as LinkIcon, LinkOff } from '@mui/icons-material'
import { Box, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material'
import { AlertDialog } from '@ui/feedback'
import { getCommentPlanLabel, getCommentStoreyLabel } from './utils'

/** Compact circular action button — mirrors the Results2 element-list row actions. */
const actionButtonSx = {
  p: 0,
  width: 22,
  height: 22,
  minWidth: 22,
  minHeight: 22,
  borderRadius: '50%',
  color: 'rgba(0,0,0,0.45)',
  '&:hover': {
    background: 'rgba(0,0,0,0.06)',
  },
} as const

interface CommentRowProps {
  comment: Comment3D
  selected: boolean
  /** Whether the linked element (if any) still exists in the model. */
  linkedElementExists: boolean
  onSelect: (id: string) => void
  onCommitText: (id: string, text: string) => void
  onDuplicate: (comment: Comment3D) => void
  onDelete: (id: string) => void
  onUnlink: (id: string) => void
}

/**
 * One comment in the list: an inline-editable text field (mirrors the Results2 "Positionsliste"
 * comment field), the plans/storeys it is shown on, and a left-aligned copy/delete action row.
 * Clicking the row selects the comment in the 3D scene; its settings are edited in the adjacent
 * CommentDrawer.
 */
const CommentRow = ({
  comment,
  selected,
  linkedElementExists,
  onSelect,
  onCommitText,
  onDuplicate,
  onDelete,
  onUnlink,
}: CommentRowProps): ReactElement => {
  const theme = useTheme()

  // A linked comment's text is owned by the "Kommentar Positionsliste" field, so
  // it can't be edited or deleted here – only unlinked.
  const isLinked = !!comment.element_guid

  const handleTextBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const next = event.target.value
    if (next !== comment.text) onCommitText(comment.id, next)
  }

  const planLabel = getCommentPlanLabel(comment)
  const storeyLabel = getCommentStoreyLabel(comment)
  const metaLabel = [planLabel, storeyLabel].filter(Boolean).join(' · ')

  // The color dot is an accent indicator; skip it for the black/Notiz color (no accent to show).
  const showColorDot = !['#000000', '#000'].includes(comment.color?.toLowerCase())

  return (
    <Box
      data-cy={`comment-row-${comment.id}`}
      onClick={() => onSelect(comment.id)}
      sx={{
        px: 1,
        py: 0.75,
        cursor: 'pointer',
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: selected ? theme.palette.action.selected : 'transparent',
        '&:hover': {
          backgroundColor: selected ? theme.palette.action.selected : theme.palette.action.hover,
        },
      }}
    >
      <TextField
        // Uncontrolled: seed from the comment, commit on blur. Keyed by id so the field resets
        // when a different comment occupies this row (e.g. after delete/duplicate). Keyed by
        // text too so an external edit (e.g. from the Positionsliste sync) reseeds the field.
        key={`${comment.id}:${comment.text}`}
        defaultValue={comment.text}
        onBlur={handleTextBlur}
        onClick={event => event.stopPropagation()}
        placeholder="Kommentar…"
        multiline
        minRows={1}
        maxRows={4}
        variant="standard"
        fullWidth
        disabled={isLinked}
        InputProps={{
          disableUnderline: true,
          readOnly: isLinked,
          sx: { fontSize: 13, p: 0 },
        }}
      />

      {(metaLabel || showColorDot) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25 }}>
          {metaLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 0, wordBreak: 'break-word', lineHeight: 1 }}
            >
              {metaLabel}
            </Typography>
          ) : null}
          {showColorDot && (
            <Box
              sx={{
                flexShrink: 0,
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: comment.color,
                border: '1px solid rgba(0,0,0,0.2)',
              }}
            />
          )}
        </Stack>
      )}

      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
        <Tooltip title="Kommentar kopieren" arrow disableInteractive>
          <IconButton
            size="small"
            data-cy={`comment-duplicate-${comment.id}`}
            onClick={event => {
              event.stopPropagation()
              onDuplicate(comment)
            }}
            sx={actionButtonSx}
          >
            <ContentCopy sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        {isLinked && (
          <Tooltip
            title={
              linkedElementExists
                ? 'Verknüpfung mit Element aufheben'
                : 'Referenziertes Element existiert nicht mehr – klicken, um die Verknüpfung aufzuheben'
            }
            arrow
            disableInteractive
          >
            <IconButton
              size="small"
              data-cy={`comment-unlink-${comment.id}`}
              onClick={event => {
                event.stopPropagation()
                onUnlink(comment.id)
              }}
              sx={actionButtonSx}
            >
              {linkedElementExists ? (
                <LinkIcon sx={{ fontSize: 15 }} />
              ) : (
                <LinkOff color="warning" sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        {/* AlertDialog opens by cloning its direct child and injecting onClick, so the IconButton
            must be that direct child (a Tooltip wouldn't forward the click). The Tooltip wraps a
            span outside, since AlertDialog renders a fragment a Tooltip can't ref directly. */}
        {isLinked ? (
          <Tooltip
            title="Kann nicht gelöscht werden, solange mit einem Element verknüpft"
            arrow
            disableInteractive
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <IconButton
                size="small"
                data-cy={`comment-delete-${comment.id}`}
                disabled
                sx={actionButtonSx}
              >
                <Delete sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title="Kommentar löschen" arrow disableInteractive>
            <Box component="span" sx={{ display: 'inline-flex' }}>
              <AlertDialog
                title="Kommentar löschen?"
                text={`Möchten Sie den Kommentar "${comment.text}" wirklich löschen?`}
                onConfirm={() => onDelete(comment.id)}
              >
                <IconButton
                  size="small"
                  data-cy={`comment-delete-${comment.id}`}
                  sx={actionButtonSx}
                >
                  <Delete sx={{ fontSize: 15 }} />
                </IconButton>
              </AlertDialog>
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Box>
  )
}

export default CommentRow
